import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";

import API, { UPLOADS_URL } from "../../services/api";
import {
  ClipboardList, RefreshCw, Search, X, CheckCircle2, XCircle, Clock,
  FileText, Trash2, Check,
} from "lucide-react";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const DS = {
  indigo: "#4F46E5",
  indigoMid: "#6366F1",
  indigoLight: "#EEF2FF",
  indigoDark: "#3730A3",
  teal: "#0D9488",
  tealLight: "#F0FDFA",
  emerald: "#059669",
  emeraldLight: "#ECFDF5",
  amber: "#D97706",
  amberLight: "#FFFBEB",
  rose: "#E11D48",
  roseLight: "#FFF1F2",
  violet: "#7C3AED",
  violetLight: "#F5F3FF",
  orange: "#EA580C",
  orangeLight: "#FFF7ED",
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  border: "#E2E8F0",
  dark: "#0F172A",
  gray: "#64748B",
  grayLight: "#F1F5F9",
  faint: "#94A3B8",
};

const ROWS_PER_PAGE = 10;

// ── GLOBAL CSS ────────────────────────────────────────────────────────────────
const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .ra-wrap *, .ra-wrap *::before, .ra-wrap *::after { box-sizing: border-box; }
  .ra-wrap {
    font-family: 'Montserrat', sans-serif;
    background: #F8FAFC;
    min-height: 100%;
  }

  /* Search input */
 .ra-search-input { font-family: 'Montserrat', sans-serif !important; }
  .ra-search-input::placeholder { color: #94A3B8; }
  .ra-search-input:focus { outline: none; }

  /* Table rows */
  .ra-tr { transition: background 0.12s; }
  .ra-tr:hover { background: #F8FAFC !important; }
  .ra-tr:hover .ra-row-num { background: #4F46E5 !important; color: #fff !important; }

 /* Approve button — light style, matches ProjectList ActionBtn look */
  .ra-btn-approve {
    display: inline-flex; align-items: center; gap: 4px;
    background: #ECFDF5; color: #059669;
    border: 1px solid #A7F3D0; padding: 7px 13px;
    border-radius: 9px; font-size: 11px; font-weight: 700;
    cursor: pointer; font-family: 'Montserrat', sans-serif;
    transition: all 0.18s;
    white-space: nowrap;
  }
  .ra-btn-approve:hover {
    background: #059669; color: #fff; border-color: #059669;
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(5,150,105,0.35);
  }

 /* Reject button — light style, matches ProjectList ActionBtn look */
  .ra-btn-reject {
    display: inline-flex; align-items: center; gap: 4px;
    background: #FFF1F2; color: #E11D48;
    border: 1px solid #FECDD3;
    padding: 7px 13px; border-radius: 9px;
    font-size: 11px; font-weight: 700;
    cursor: pointer; font-family: 'Montserrat', sans-serif;
    transition: all 0.18s;
    white-space: nowrap;
  }
  .ra-btn-reject:hover {
    background: #E11D48; color: #fff; border-color: #E11D48;
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(225,29,72,0.32);
  }

/* Delete button — light style, matches ProjectList ActionBtn look */
  .ra-btn-delete {
    display: inline-flex; align-items: center; gap: 4px;
    background: #FCEAED; color: #C8102E;
    border: 1px solid #F8C8D0;
    padding: 7px 13px; border-radius: 9px;
    font-size: 11px; font-weight: 700;
    cursor: pointer; font-family: 'Montserrat', sans-serif;
    transition: all 0.18s;
    white-space: nowrap;
  }
  .ra-btn-delete:hover {
    background: #C8102E; color: #fff; border-color: #C8102E;
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(200,16,46,0.32);
  }

  /* View file link */
  .ra-file-link {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11.5px; font-weight: 700;
    color: #7C3AED; background: #F5F3FF;
    padding: 5px 11px; border-radius: 8px;
    text-decoration: none; border: 1px solid #DDD6FE;
   font-family: 'Montserrat', sans-serif;
    transition: all 0.14s;
  }
  .ra-file-link:hover {
    background: #7C3AED; color: #fff;
    border-color: #7C3AED;
  }

  /* Stat card hover */
  .ra-stat-card { transition: box-shadow 0.2s, transform 0.2s; }
  .ra-stat-card:hover { box-shadow: 0 12px 32px rgba(0,0,0,0.09) !important; transform: translateY(-2px); }

  /* Refresh button */
  .ra-btn-refresh {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff; color: #4F46E5;
    border: 1.5px solid #C7D2FE; border-radius: 10px;
    padding: 8px 16px; font-size: 13px; font-weight: 700;
   cursor: pointer; font-family: 'Montserrat', sans-serif;
    box-shadow: 0 1px 4px rgba(79,70,229,0.08);
    transition: all 0.15s;
  }
  .ra-btn-refresh:hover {
    background: #EEF2FF;
    border-color: #A5B4FC;
    box-shadow: 0 4px 12px rgba(79,70,229,0.15);
    transform: translateY(-1px);
  }
  .ra-btn-refresh:active .ra-refresh-icon { transform: rotate(180deg); }
  .ra-refresh-icon { transition: transform 0.4s; font-size: 15px; }

  /* Pagination button */
  .ra-pg-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    font-size: 13px; font-weight: 700;
    cursor: pointer; font-family: 'Montserrat', sans-serif;
    border: 1.5px solid #E2E8F0;
    background: #fff; color: #64748B;
    transition: all 0.14s;
  }
  .ra-pg-btn:hover:not(:disabled) {
    border-color: #A5B4FC; color: #4F46E5; background: #EEF2FF;
  }
  .ra-pg-btn:disabled { opacity: 0.38; cursor: not-allowed; }
  .ra-pg-btn.active {
    background: linear-gradient(135deg,#4F46E5,#6366F1);
    color: #fff; border-color: transparent;
    box-shadow: 0 3px 10px rgba(79,70,229,0.3);
  }

  /* Scrollbar */
  .ra-table-scroll::-webkit-scrollbar { height: 5px; }
  .ra-table-scroll::-webkit-scrollbar-track { background: transparent; }
  .ra-table-scroll::-webkit-scrollbar-thumb { background: #DDD6FE; border-radius: 99px; }

  @media (max-width: 1024px) {
    .ra-stats-grid { grid-template-columns: repeat(2,1fr) !important; }
  }
  @media (max-width: 768px) {
    .ra-stats-grid { grid-template-columns: 1fr 1fr !important; }
    .ra-page-header { flex-direction: column !important; align-items: flex-start !important; }
    .ra-toolbar { flex-direction: column !important; align-items: stretch !important; }
    .ra-search-box { min-width: unset !important; width: 100% !important; }
  }
  @media (max-width: 480px) {
    .ra-stats-grid { grid-template-columns: 1fr 1fr !important; }
  }
`;

export default function ReportApproval() {
  // ── STATE ──────────────────────────────────────────────────────────────────
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState(null); // { message, type: "success" | "error" | "delete" }
  // ── STATE ── (add this alongside your existing useState calls)
  const [actionLoading, setActionLoading] = useState({}); // { [reportId]: "approve" | "reject" | "delete" }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── API CALLS (UNCHANGED) ──────────────────────────────────────────────────
  const fetchReports = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const isAdminUser = Number(localStorage.getItem("is_admin")) === 1;
      const storedRole = (localStorage.getItem("role") || "").toLowerCase();
      const isSuperAdmin = isAdminUser || storedRole === "admin" || storedRole === "super_admin";
      const api = isSuperAdmin
        ? "/reports"
        : `/reports/managerwise/${localStorage.getItem("id")}`;
      const res = await API.get(api, {
        headers: {
          "Cache-Control": "no-cache",
          "Pragma": "no-cache",
        },
        params: { _t: Date.now() },
      });
      setReports([]);
      setTimeout(() => {
        setReports(res.data.report || res.data.reports || []);
        setCurrentPage(1);
      }, 50);
    } catch (err) {
      console.error("Error reading system project reports:", err);
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []);

  // ── approveReport — wrap with loading state ──
  const approveReport = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "approve" }));
    try {
      const res = await API.patch(`/reports/admin/${id}/approve`);
      console.log("Approve API Response:", res);
      if (res.data?.success) {
        setToast({ message: "Report approved successfully!", type: "success" });
        await fetchReports();
      } else {
        setToast({ message: res.data?.message || "Approval failed.", type: "error" });
      }
    } catch (err) {
      console.log("Approve Error:", err);
      setToast({ message: err.response?.data?.message || "Failed connecting with server to approve report.", type: "error" });
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  // ── rejectReport — wrap with loading state ──
  const rejectReport = async (id) => {
    setActionLoading((prev) => ({ ...prev, [id]: "reject" }));
    try {
      const res = await API.patch(`/reports/admin/${id}/reject`);
      if (res.data.success) {
        setToast({ message: "Report status set to rejected.", type: "error" });
        setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status: "rejected" } : r)));
      } else {
        setToast({ message: res.data.message || "Rejection failed.", type: "error" });
      }
    } catch (err) {
      setToast({ message: "Failed to reject report.", type: "error" });
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  // ── deleteReport — wrap with loading state ──
  const deleteReport = async (id) => {
    if (window.confirm("Are you sure you want to delete this report permanently?")) {
      setActionLoading((prev) => ({ ...prev, [id]: "delete" }));
      try {
        const res = await API.delete(`/reports/${id}`);
        if (res.data.success) {
          setToast({ message: "Report deleted successfully.", type: "delete" });
          fetchReports();
        }
      } catch (err) {
        setToast({ message: "Failed to drop report record from database.", type: "delete" });
      } finally {
        setActionLoading((prev) => { const n = { ...prev }; delete n[id]; return n; });
      }
    }
  };

  // ── DERIVED DATA ───────────────────────────────────────────────────────────
  // CHANGE 1: KPI counts use full reports array (unchanged)
  const pendingCount = reports.filter(r => (r.status || "").toLowerCase() === "pending").length;
  const approvedCount = reports.filter(r => (r.status || "").toLowerCase() === "approved").length;
  const rejectedCount = reports.filter(r => (r.status || "").toLowerCase() === "rejected").length;

  // CHANGE 1: Table shows ONLY pending reports, then search filters within those
  const pendingReports = reports.filter(r => (r.status || "pending").toLowerCase() === "pending");

  const filteredReports = pendingReports.filter(r =>
    (r.project_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.engineer_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.title || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.report_type || "").toLowerCase().includes(search.toLowerCase())
  );

  // CHANGE 7: Pagination
  const totalPages = Math.max(1, Math.ceil(filteredReports.length / ROWS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * ROWS_PER_PAGE;
  const pageRows = filteredReports.slice(pageStart, pageStart + ROWS_PER_PAGE);

  const handleSearchChange = (val) => { setSearch(val); setCurrentPage(1); };

  // Pagination page numbers (show up to 5 pages)
  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, safePage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  };

  // ── STAT CARDS — now styled to match ProjectReports.jsx KPI cards ─────────
  const statCards = [
    { label: "Total Submissions", value: reports.length, color: DS.indigoMid, icon: <ClipboardList size={20} />, bg: `linear-gradient(135deg,${DS.indigoLight},#EEE8FF)`, border: "#C7D2FE" },
    { label: "Pending Review", value: pendingCount, color: DS.amber, icon: <Clock size={20} />, bg: `linear-gradient(135deg,${DS.amberLight},#FFF3DC)`, border: "#FDE68A" },
    { label: "Approved Reports", value: approvedCount, color: DS.emerald, icon: <CheckCircle2 size={20} />, bg: `linear-gradient(135deg,${DS.emeraldLight},#E0FAF0)`, border: "#A7F3D0" },
    { label: "Rejected Reports", value: rejectedCount, color: DS.rose, icon: <XCircle size={20} />, bg: `linear-gradient(135deg,${DS.roseLight},#FFE8EC)`, border: "#FECDD3" },
  ];

  // ── TABLE HEADERS ──────────────────────────────────────────────────────────
  const tableHeaders = ["#", "Project & Engineer", "Category / Title", "Details", "File", "Actions"];

  // ─────────────────────────────────────────────────────────────────────────
  const TOAST_STYLES = {
    success: { bg: "#1A7A42", icon: <CheckCircle2 size={16} /> },
    error: { bg: "#C8102E", icon: <X size={16} /> },
    delete: { bg: "#4b5563", icon: <Trash2 size={16} /> },
  };

  return (
    <AdminLayout>
      <style>{globalCss}</style>
      <style>{`@keyframes ra-toast-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }`}</style>

      {toast && (
        <div style={{
          position: "fixed", top: 24, right: 24, zIndex: 99999,
          background: TOAST_STYLES[toast.type]?.bg || TOAST_STYLES.success.bg,
          color: "#fff", padding: "14px 18px", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          display: "flex", alignItems: "center", gap: 12,
          minWidth: 280, maxWidth: 380,
          animation: "ra-toast-in 0.22s ease",
          fontFamily: "'Montserrat', sans-serif",
        }}>
          <span style={{ display: "inline-flex", flexShrink: 0 }}>
            {TOAST_STYLES[toast.type]?.icon || <CheckCircle2 size={16} />}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{toast.message}</div>
          </div>
          <button onClick={() => setToast(null)} style={{
            background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
            width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
            fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}><X size={12} /></button>
        </div>
      )}

      <div className="ra-wrap" style={{ flex: 1, padding: "28px 28px 40px", overflowY: "auto" }}>

        {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
        {/* CHANGE 2: Live badge removed */}
        <div className="ra-page-header" style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 12, marginBottom: 28,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 15,
              background: "linear-gradient(135deg,#4F46E5,#6366F1)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 6px 18px rgba(79,70,229,0.3)",
            }}>
              <ClipboardList size={22} color="#fff" />
            </div>
            <div>
              <h1 style={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 24,
                color: DS.dark, margin: 0, letterSpacing: "-0.4px",
              }}>
                Report Approval
              </h1>
              <div style={{ fontSize: 13, color: DS.gray, marginTop: 3, fontWeight: 400, fontFamily: "'Montserrat', sans-serif" }}>
                Approve or reject project progress reports submitted by engineers
              </div>
            </div>
          </div>

          {/* CHANGE 3: Refresh button — ProjectList style */}
          {/* Refresh button — icon style matching ProjectReports */}
          <button
            className="ra-refresh"
            onClick={async () => {
              setRefreshing(true);
              const minSpin = new Promise(resolve => setTimeout(resolve, 700));
              await Promise.all([fetchReports(true), minSpin]);
              setRefreshing(false);
            }}
            disabled={refreshing}
            title="Refresh"
            style={{
              width: 40, height: 40, borderRadius: 11,
              border: `1.5px solid ${DS.border}`,
              background: DS.surface, color: DS.gray,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              cursor: refreshing ? "not-allowed" : "pointer",
              opacity: refreshing ? 0.7 : 1,
            }}
          >
            <span style={{
              display: "inline-flex",
              animation: refreshing ? "ra-spin 0.7s linear infinite" : "none",
            }}>
              <RefreshCw size={16} />
            </span>
          </button>
        </div>

        {/* spin keyframe injected inline */}
        <style>{`@keyframes ra-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

        {/* ── STAT CARDS — matching ProjectReports.jsx KPI card structure ──── */}
        <div className="ra-stats-grid" style={{
          display: "grid", gridTemplateColumns: "repeat(4,1fr)",
          gap: 16, marginBottom: 26,
        }}>
          {statCards.map((s) => (
            <div key={s.label} className="ra-stat-card" style={{
              background: DS.surface, borderRadius: 16,
              border: `1px solid ${DS.border}`, padding: "20px 22px",
              display: "flex", alignItems: "center", gap: 14,
              boxShadow: "0 2px 12px rgba(79,70,229,0.06)",
              position: "relative", overflow: "hidden",
            }}>
              {/* Background accent blob — same as ProjectReports */}
              <div style={{
                position: "absolute", right: -10, top: -10,
                width: 70, height: 70, borderRadius: "50%",
                background: s.bg, opacity: 0.6,
              }} />
              <div style={{
                width: 46, height: 46, borderRadius: 13,
                background: s.bg, border: `1.5px solid ${s.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0, position: "relative",
              }}>{s.icon}</div>
              <div style={{ position: "relative" }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: DS.faint,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                  marginBottom: 3, fontFamily: "'Montserrat', sans-serif",
                }}>
                  {s.label}
                </div>
                <div style={{
                  fontWeight: 800, fontSize: 28, color: s.color,
                  lineHeight: 1, fontFamily: "'Montserrat', sans-serif",
                }}>
                  {loading ? "—" : s.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── TABLE CARD ──────────────────────────────────────────────────── */}
        <div style={{
          background: DS.surface, borderRadius: 20,
          border: `1px solid ${DS.border}`, overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}>
          {/* Rainbow top bar */}
          <div style={{ height: 3, background: "linear-gradient(90deg,#4F46E5 0%,#7C3AED 33%,#E11D48 66%,#D97706 100%)" }} />

          {/* ── TOOLBAR ─────────────────────────────────────────────────── */}
          {/* CHANGE 6: ProjectList-style search */}
          <div className="ra-toolbar" style={{
            padding: "16px 22px", borderBottom: `1px solid ${DS.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12, background: "#FDFCFF",
          }}>
            <div>
              <div style={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 700,
                fontSize: 15, color: DS.dark,
              }}>
                Pending Submissions
              </div>
              <div style={{ fontSize: 12, color: DS.faint, marginTop: 2, fontFamily: "'Montserrat', sans-serif" }}>
                {filteredReports.length} pending report{filteredReports.length !== 1 ? "s" : ""} awaiting review
              </div>
            </div>

            {/* Search box */}
            <div
              className="ra-search-box"
              style={{
                display: "flex", alignItems: "center", gap: 8,
                background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`,
                borderRadius: 12, padding: "9px 14px", minWidth: 300,
                transition: "border-color 0.15s",
              }}
              onFocus={e => e.currentTarget.style.borderColor = "#A5B4FC"}
              onBlur={e => e.currentTarget.style.borderColor = DS.border}
            >
              <Search size={15} color={DS.faint} style={{ flexShrink: 0 }} />
              <input
                className="ra-search-input"
                type="text"
                placeholder="Search project, engineer, category…"
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                style={{
                  border: "none", background: "transparent",
                  fontSize: 13, color: DS.dark, outline: "none",
                  width: "100%", fontFamily: "'Montserrat', sans-serif",
                }}
              />
              {search && (
                <button
                  onClick={() => handleSearchChange("")}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: DS.faint, padding: 0, lineHeight: 1, display: "flex",
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* ── TABLE ───────────────────────────────────────────────────── */}
          {/* CHANGE 8: Standardized table styling */}
          <div className="ra-table-scroll" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr style={{ background: "#FAFBFF", borderBottom: `2px solid ${DS.border}` }}>
                  {tableHeaders.map((h, i) => (
                    <th key={h} style={{
                      fontSize: 10, fontWeight: 700, color: DS.faint,
                      textTransform: "uppercase", letterSpacing: "0.1em",
                      padding: "11px 18px",
                      textAlign: i === 0 ? "center" : "left",
                      fontFamily: "'Montserrat', sans-serif",
                      userSelect: "none",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* ── Loading ── */}
                {loading && (
                  <tr>
                    <td colSpan={tableHeaders.length} style={{ padding: "64px 20px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                        <div style={{
                          width: 52, height: 52, borderRadius: 14,
                          background: DS.indigoLight,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <ClipboardList size={22} color={DS.indigo} />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: DS.gray, fontFamily: "'Montserrat', sans-serif" }}>
                          Loading pending reports…
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* ── Empty state ── */}
                {!loading && filteredReports.length === 0 && (
                  <tr>
                    <td colSpan={tableHeaders.length} style={{ padding: "64px 20px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 56, height: 56, borderRadius: 16,
                          background: DS.amberLight, border: `1px solid #FDE68A`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {search ? <Search size={24} color={DS.amber} /> : <CheckCircle2 size={24} color={DS.amber} />}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: DS.dark, fontFamily: "'Montserrat', sans-serif" }}>
                          {search ? "No matching reports" : "No pending reports"}
                        </div>
                        <div style={{ fontSize: 13, color: DS.faint, fontFamily: "'Montserrat', sans-serif" }}>
                          {search
                            ? `No pending reports match "${search}" — try a different keyword.`
                            : "All reports have been reviewed. Nothing is waiting for approval."}
                        </div>
                        {search && (
                          <button
                            onClick={() => handleSearchChange("")}
                            style={{
                              marginTop: 6, background: DS.indigoLight, color: DS.indigo,
                              border: `1px solid #C7D2FE`, borderRadius: 9,
                              padding: "7px 16px", fontSize: 12, fontWeight: 700,
                              cursor: "pointer", fontFamily: "'Montserrat', sans-serif",
                            }}
                          >
                            Clear Search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {/* ── Rows ── */}
                {/* CHANGE 5: Serial number based on filtered + paginated index */}
                {!loading && pageRows.map((report, i) => (
                  <tr
                    key={report.id}
                    className="ra-tr"
                    style={{ borderBottom: `1px solid #F1F5F9`, background: DS.surface }}
                  >
                    {/* # — CHANGE 5 */}
                    <td style={{ padding: "15px 18px", textAlign: "center", width: 52 }}>
                      <span className="ra-row-num" style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 28, height: 28, borderRadius: 8,
                        background: DS.indigoLight, color: DS.indigo,
                        fontSize: 12, fontWeight: 700,
                        fontFamily: "'Montserrat', sans-serif",
                        transition: "background 0.12s, color 0.12s",
                      }}>
                        {pageStart + i + 1}
                      </span>
                    </td>

                    {/* Project & Engineer */}
                    <td style={{ padding: "15px 18px" }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: DS.dark,
                        fontFamily: "'Montserrat', sans-serif", letterSpacing: "-0.1px",
                      }}>
                        {report.project_name || `Proj ID: ${report.project_id}`}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: 5,
                          background: DS.indigoLight,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9, fontWeight: 700, color: DS.indigo, flexShrink: 0,
                        }}>
                          {(report.engineer_name || "E")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 11.5, color: DS.gray, fontWeight: 500, fontFamily: "'Montserrat', sans-serif" }}>
                          {report.engineer_name || "Engineer"}
                        </span>
                      </div>
                    </td>

                    {/* Category / Title */}
                    <td style={{ padding: "15px 18px" }}>
                      <span style={{
                        display: "inline-block", fontSize: 10, fontWeight: 700,
                        color: DS.indigo, background: DS.indigoLight,
                        padding: "3px 9px", borderRadius: 20, marginBottom: 5,
                        letterSpacing: "0.04em", fontFamily: "'Montserrat', sans-serif",
                      }}>
                        {report.report_type || "General"}
                      </span>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: DS.dark,
                        fontFamily: "'Montserrat', sans-serif",
                      }}>
                        {report.title || "—"}
                      </div>
                    </td>

                    {/* Details */}
                    <td style={{ padding: "15px 18px", maxWidth: 200 }}>
                      <div style={{
                        fontSize: 12, color: DS.gray, lineHeight: 1.5,
                        fontFamily: "'Montserrat', sans-serif",
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {report.details || "—"}
                      </div>
                    </td>

                    {/* File */}
                    <td style={{ padding: "15px 18px" }}>
                      {report.file_url ? (
                        <a
                          href={`${UPLOADS_URL}${report.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="ra-file-link"
                        >
                          <FileText size={12} />
                        </a>
                      ) : (
                        <span style={{ fontSize: 12, color: DS.faint }}>—</span>
                      )}
                    </td>

                    {/* Actions — CHANGE 9: improved button design */}
                    {/* Actions — disable the other two buttons while one action is in flight */}
                    <td style={{ padding: "15px 18px" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
                        <button
                          className="ra-btn-approve"
                          onClick={() => approveReport(report.id)}
                          disabled={!!actionLoading[report.id]}
                          style={{ opacity: actionLoading[report.id] && actionLoading[report.id] !== "approve" ? 0.4 : 1, cursor: actionLoading[report.id] ? "not-allowed" : "pointer" }}
                        >
                          <Check size={12} />
                        </button>
                        <button
                          className="ra-btn-reject"
                          onClick={() => rejectReport(report.id)}
                          disabled={!!actionLoading[report.id]}
                          style={{ opacity: actionLoading[report.id] && actionLoading[report.id] !== "reject" ? 0.4 : 1, cursor: actionLoading[report.id] ? "not-allowed" : "pointer" }}
                        >
                          <X size={12} />
                        </button>
                        <button
                          className="ra-btn-delete"
                          onClick={() => deleteReport(report.id)}
                          disabled={!!actionLoading[report.id]}
                          style={{ opacity: actionLoading[report.id] && actionLoading[report.id] !== "delete" ? 0.4 : 1, cursor: actionLoading[report.id] ? "not-allowed" : "pointer" }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── TABLE FOOTER + PAGINATION ──────────────────────────────── */}
          {/* CHANGE 7: ProjectList-style pagination */}
          <div style={{
            padding: "13px 22px", borderTop: `1px solid ${DS.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#FAFBFF", flexWrap: "wrap", gap: 10,
          }}>
            {/* Row count */}
            <span style={{ fontSize: 12, color: DS.faint, fontFamily: "'Montserrat', sans-serif" }}>
              Showing{" "}
              <strong style={{ color: DS.dark }}>
                {filteredReports.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + ROWS_PER_PAGE, filteredReports.length)}
              </strong>
              {" "}of{" "}
              <strong style={{ color: DS.dark }}>{filteredReports.length}</strong>
              {" "}pending report{filteredReports.length !== 1 ? "s" : ""}
            </span>

            {/* Page buttons */}
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {/* Prev */}
                <button
                  className="ra-pg-btn"
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  title="Previous page"
                >
                  ‹
                </button>

                {/* Page numbers */}
                {getPageNumbers().map(p => (
                  <button
                    key={p}
                    className={`ra-pg-btn${p === safePage ? " active" : ""}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                ))}

                {/* Next */}
                <button
                  className="ra-pg-btn"
                  disabled={safePage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  title="Next page"
                >
                  ›
                </button>
              </div>
            )}

            {/* Single page — static pill */}
            {totalPages <= 1 && filteredReports.length > 0 && (
              <div style={{ display: "flex", gap: 4 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: "linear-gradient(135deg,#4F46E5,#6366F1)",
                  color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
                  fontFamily: "'Montserrat', sans-serif",
                }}>1</div>
              </div>
            )}
          </div>

        </div>{/* end table card */}
      </div>
    </AdminLayout>
  );
}